import type { MediaAvailability } from '@mimir/common/dto/mediaAvailability';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { ArtifactReference } from '@mimir/primitive/artifactReference';
import { getExtname } from '@mimir/common/platformAbstraction/pathComposition';

export interface ResolutionItem {
  id: string;
  media?: MediaAvailability | null;
  originalFileName?: string | null;
  hasMediaIngestIntegrationExternalHighres?: boolean;
}

interface ItemStreamPresence {
  isAudioSegmentedTs: boolean;
  isVideoSegmentedTs: boolean;
  hasHighResVideoTsSegments: boolean;
  hasEditProxyFull: boolean;
  canUseTruncatedMp4: boolean;
}

function resolveItemStreamPresence(item: ResolutionItem): ItemStreamPresence {
  const codecsByStreamId = item.media?.codecsByStreamId;
  const segmentCount = item.media?.segmentCount ?? 0;

  const isAudioSegmentedTs = codecsByStreamId?.[StreamRoleId.LiveRecordAudioOnlyHighresTs] !== undefined;
  const isVideoSegmentedTs = codecsByStreamId?.[StreamRoleId.LiveRecordWebproxyTs] !== undefined;
  const hasHighResVideoTsSegments = codecsByStreamId?.[StreamRoleId.LiveRecordHighresTs] !== undefined;
  const hasEditProxyFull = codecsByStreamId?.[StreamRoleId.EditProxyFull] !== undefined;

  return {
    isAudioSegmentedTs,
    isVideoSegmentedTs,
    hasHighResVideoTsSegments,
    hasEditProxyFull,
    canUseTruncatedMp4: hasEditProxyFull && segmentCount > 0,
  };
}

export function isFlatHighresTsItem(item: ResolutionItem): boolean {
  const streams = resolveItemStreamPresence(item);
  return streams.isVideoSegmentedTs && !(streams.hasHighResVideoTsSegments || item.hasMediaIngestIntegrationExternalHighres);
}

/**
 * Returns the explicit artifactRef if present, otherwise derives one from the item's
 * media availability. Returns null only when both are null.
 */
export function resolveArtifactReference(
  artifactRef: ArtifactReference | null,
  item: ResolutionItem | null,
): ArtifactReference | null {
  if (artifactRef !== null || item === null) {
    return artifactRef;
  }
  return deriveArtifactReferenceFromItem(item);
}

/**
 * Derives an ArtifactReference for an item by inspecting its media availability.
 * Use this when no explicit ArtifactReference is stored (e.g. the project item
 * has no Mimir.Artifact metadata, or it held a legacy ImplicitReference).
 */
export function deriveArtifactReferenceFromItem(
  item: ResolutionItem,
): ArtifactReference {
  const streams = resolveItemStreamPresence(item);
  const segmentCount = item.media?.segmentCount ?? 0;

  if (streams.isAudioSegmentedTs) {
    return {
      type: 'AudioTsSegmentRange',
      itemId: item.id,
      segmentTimes: {
        first: 0,
        last: item.media?.segmentCount === 0 ? 0 : segmentCount - 1, // Live item that has not recevied segments yet can have segment count of 0
      },
      remuxTo: undefined,
    };
  }

  if (streams.isVideoSegmentedTs || streams.hasHighResVideoTsSegments) {
    const highResExt = streams.isVideoSegmentedTs && !streams.hasHighResVideoTsSegments && item.originalFileName
      ? getExtname(item.originalFileName, '/')
      : undefined;
    return {
      type: 'TsSegmentRange',
      itemId: item.id,
      segmentTimes: {
        first: 0,
        last: item.media?.segmentCount === 0 ? 0 : segmentCount - 1, // Live item that has not recevied segments yet can have segment count of 0
      },
      highResExt,
    };
  }

  if (streams.canUseTruncatedMp4) {
    return {
      type: 'TruncatedMp4',
      itemId: item.id,
      // segmentCount excludes the init segment (see MediaAvailability.segmentCount), so
      // blob index of the last media segment = segmentCount - 1 + 1 (init offset) = segmentCount.
      lastSegmentIndex: segmentCount,
    };
  }

  // Flat file item: no TS media streams detected.
  return {
    type: 'FlatFileReference',
    itemId: item.id,
  };
}
